import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Fade } from "@mui/material"
import { useParams } from "@tanstack/react-router"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import type { FunctionComponent } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { z } from "zod"

import { COMPANY_EDIT_ROUTE } from "../../constants"
import EditAssociatedLocations from "../../edit/components/EditAssociatedLocations"

import AssociatedLocation from "./AssociatedLocation"
import AssociatedLocationsTable from "./AssociatedLocationsTable"

import type {
  CompanyLocationAssociationCreateInput,
  CompanyLocationAssociationOutput,
  CompanyLocationAssociationUpdateInput,
} from "@/graphql/codegen/graphql"
import { useGetAssociatedLocations } from "@/screens/Companies/hooks"

const AssociatedLocationSchema = z.object({
  shipping: z.string().min(1, "Select shipping"),
  billing: z.string().min(1, "Select billing"),
  order: z.string().min(1, "Select order"),
  office: z.string().optional(),
  shipVia: z.string().optional(),
})

interface AssociatedLocationContainerProps {
  associatedLocationList: CompanyLocationAssociationOutput[]
  showFields: boolean
  onAddFields: (showFields: boolean) => void
  reexecuteAssociatedLocationQuery: () => void
}

const defaultValues = {
  shipping: "",
  billing: "",
  order: "",
  office: "",
  shipVia: "",
}

const AssociatedLocationContainer: FunctionComponent<AssociatedLocationContainerProps> = ({
  associatedLocationList,
  showFields,
  onAddFields,
  reexecuteAssociatedLocationQuery,
}) => {
  const { companyId } = useParams({ from: COMPANY_EDIT_ROUTE })
  const [showEditLocationDialog, setShowEditLocationDialog] = useState<boolean>(false)
  const [activeEditLocation, setActiveEditLocation] = useState<CompanyLocationAssociationOutput | undefined>()
  const methods = useForm({
    resolver: zodResolver(AssociatedLocationSchema),
    defaultValues,
    mode: "all",
  })

  const {
    createCompanyLocationAssociation,
    locations,
    companyNameList,
    loadMoreLocations,
    loadMoreCompanies,
    updateCompanyLocationAssociation,
    fetching,
    setSearchTerm,
  } = useGetAssociatedLocations({
    companyId,
  })

  const handleScrollLocations = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
      if (scrollTop + clientHeight >= scrollHeight) {
        loadMoreLocations()
      }
    },
    [loadMoreLocations]
  )

  const { setError, handleSubmit, reset } = methods

  const billingLocationId = associatedLocationList.length > 0 ? associatedLocationList[0]?.billing?.locationId : ""

  useEffect(() => {
    if (billingLocationId && showFields) {
      reset({ ...defaultValues, billing: billingLocationId })
    }
  }, [billingLocationId, reset, showFields])

  const onSubmit = handleSubmit(async (data) => {
    const formattedData: CompanyLocationAssociationCreateInput = {
      companyId,
      shipping: {
        locationId: data.shipping,
      },
      billing: {
        locationId: data.billing,
      },
      order: {
        locationId: data.order,
      },
      ...(data.shipVia && {
        shippingVia: {
          companyId: data.shipVia,
        },
      }),
      ...(data.office && {
        office: {
          locationId: data.office,
        },
      }),
    }

    if (!createCompanyLocationAssociation) {
      return
    }

    const { error } = await createCompanyLocationAssociation({ input: formattedData })
    onAddFields(false)
    if (error) {
      // Handle any errors that might occur during the associating a location with a company
      setError("shipping", {
        type: "server",
        message: error.message,
      })
      onAddFields(false)
    }
    methods.reset()
    reexecuteAssociatedLocationQuery()
  })

  const onEditSubmit = handleSubmit(async (data) => {
    const formattedData: CompanyLocationAssociationUpdateInput = {
      companyId,
      companyLocationAssociationId: activeEditLocation?.companyLocationAssociationId ?? "",
      shipping: {
        locationId: data.shipping,
      },
      order: {
        locationId: data.order,
      },
      billing: {
        locationId: data.billing,
      },
      shippingVia: data.shipVia ? { companyId: data.shipVia } : null,
      office: data.office ? { locationId: data.office } : null,
    }

    if (!updateCompanyLocationAssociation) {
      return
    }

    const { error } = await updateCompanyLocationAssociation({ input: formattedData })
    onAddFields(false)
    if (error) {
      // Handle any errors that might occur during the associating a location with a company
      setError("shipping", {
        type: "server",
        message: error.message,
      })
      onAddFields(false)
    }
    setShowEditLocationDialog(false)
    methods.reset()
    setActiveEditLocation(undefined)
    reexecuteAssociatedLocationQuery()
  })

  const currentAssociation = useMemo(
    () =>
      associatedLocationList
        ?.find((loc) => loc?.companyLocationAssociationId === activeEditLocation?.companyLocationAssociationId)
        ?.companyDetail?.company?.locationsAssociations?.find(
          (loc) => loc.companyLocationAssociationId === activeEditLocation?.companyLocationAssociationId
        ),
    [activeEditLocation, associatedLocationList]
  )

  return (
    <FormProvider {...methods}>
      <Box className='h-full overflow-y-auto'>
        <Fade in={!fetching} timeout={500}>
          <div>
            {showFields && (
              <AssociatedLocation
                handleScrollLocations={handleScrollLocations}
                locations={locations}
                companyNameList={companyNameList}
                onSubmit={onSubmit}
                isBillingFieldDisabled={!!billingLocationId}
                setSearchTerm={setSearchTerm}
                loadMoreCompanies={loadMoreCompanies}
              />
            )}
            <AssociatedLocationsTable
              associatedLocationList={associatedLocationList}
              onEditClick={(associatedLocation) => {
                onAddFields(false)
                methods.reset({
                  shipping: associatedLocation?.shipping.locationId,
                  billing: associatedLocation?.billing.locationId,
                  order: associatedLocation?.order?.locationId,
                  office: associatedLocation?.office?.locationId,
                  shipVia: associatedLocation?.shippingVia?.companyId,
                })
                setActiveEditLocation(associatedLocation)
                setShowEditLocationDialog(true)
              }}
            />
            <EditAssociatedLocations
              open={showEditLocationDialog}
              onClose={() => {
                methods.reset(defaultValues)
                setActiveEditLocation(undefined)
                setShowEditLocationDialog(false)
              }}
              locations={locations}
              companyNameList={companyNameList}
              handleScrollLocations={handleScrollLocations}
              onSubmit={onEditSubmit}
              currentAssociation={currentAssociation}
              setSearchTerm={setSearchTerm}
              loadMoreCompanies={loadMoreCompanies}
            />
          </div>
        </Fade>
      </Box>
    </FormProvider>
  )
}

export default AssociatedLocationContainer
